// A tailoring run is driven step by step from this tab, so Chrome must not
// suspend or discard the tab while the user is elsewhere. Chrome protects a tab
// that holds a Web Lock from freezing, and a tab playing sound from freezing,
// throttling and Memory Saver. The tone is far too quiet and too low to hear.
const MAX_HOLD_MS = 45 * 60_000; // same limit as a resumable run
const TONE_HZ = 30;
const TONE_GAIN = 0.002;

let held: null | { stop: () => void } = null;

export function holdTabAwake(): void {
  if (held) return;
  let unlock = () => {};
  try {
    navigator.locks?.request(`tw-run-${Date.now()}`, () => new Promise<void>((res) => { unlock = res; }));
  } catch (e) { console.warn('Tab lock unavailable:', e); }

  let ctx: AudioContext | null = null;
  // After a reload Chrome holds audio back until the next click or key press.
  const wake = () => { ctx?.resume(); };
  try {
    ctx = new AudioContext();
    const tone = ctx.createOscillator();
    const volume = ctx.createGain();
    tone.frequency.value = TONE_HZ;
    volume.gain.value = TONE_GAIN;
    tone.connect(volume).connect(ctx.destination);
    tone.start();
    wake();
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);
  } catch (e) { console.warn('Tab audio unavailable:', e); }

  const limit = window.setTimeout(() => releaseTab(), MAX_HOLD_MS);
  held = {
    stop: () => {
      window.clearTimeout(limit);
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
      ctx?.close();
      unlock();
    },
  };
}

export function releaseTab(): void {
  held?.stop();
  held = null;
}
